<script setup lang="ts">
import { IconChevronDown } from '@tabler/icons-vue'
import { computed } from 'vue'

import BaseIcon from '../../atoms/BaseIcon.vue'

interface Props {
  enabled: boolean
  direction: 'prev' | 'next'
}

const props = defineProps<Props>()

const emit = defineEmits<{
  scroll: [delta: number]
}>()

const isPrevious = computed(() => props.direction === 'prev')

const classNames = computed(() => ({
  disabled: !props.enabled,
  'prev-month': isPrevious.value,
  'next-month': !isPrevious.value,
}))

function handleScrollButtonPress() {
  if (props.enabled) {
    emit('scroll', isPrevious.value ? -1 : 1)
  }
}
</script>

<template>
  <button
    type="button"
    :aria-label="isPrevious ? 'Previous Month' : 'Next Month'"
    class="scroll-button"
    :class="classNames"
    @click.prevent="handleScrollButtonPress"
  >
    <BaseIcon
      :class="isPrevious ? 'rotate-left' : 'rotate-right'"
      class="scroll-chevron"
      :icon="IconChevronDown"
      :stroke="1.75"
    />
  </button>
</template>

<style scoped>
.scroll-button {
  --_button-bg: var(--elevation-1);
  --_button-border: 1px solid var(--neutral-border-color);
  --_icon-color: var(--text-primary);

  position: absolute;
  z-index: 100;
  width: 40px;
  height: 40px;
  border: var(--_button-border);
  border-radius: 50%;
  text-align: center;
  padding: 0;
  background: var(--_button-bg);
  color: var(--_icon-color);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(.disabled) {
    background: var(--elevation-0);
  }

  &.disabled {
    display: none;
  }

  &.prev-month {
    top: 4px;
    left: 0;
  }

  &.next-month {
    top: 4px;
    right: 0;
  }

  .scroll-chevron {
    font-size: 10.667px;
  }

  .rotate-left {
    transform: rotate(90deg);
  }

  .rotate-right {
    transform: rotate(-90deg);
  }
}
</style>
